import React, { useContext, useEffect, useRef, useState } from "react";
import { useHistory, useLocation, useRouteMatch } from "react-router-dom";

import API from "UserProfile/Family/API";
import { UserProfileState } from "UserProfile/state";

import FnrButton from "UI/FnrButton3";
import FnrConfirmationPopover from "UI/FnrConfirmationPopover";
import FnrOverlay, {
  FnrOverlayButtonRow,
  FnrOverlayContents,
} from "UI/FnrOverlay";
import {
  SharedResizingChild,
  SharedResizingContainer,
} from "UI/SharedResizingArea";

import AddFamilyMemberByName from "./AddFamilyMemberByName";
import ConflictsConfirmation from "./ConflictsConfirmation";
import ErrorPopover from "./ErrorPopover";
import FamilyRelationshipPickerSection from "./FamilyRelationshipPickerSection";
import { UnsharedMessageContent } from "./PaymentMethodsUnshared";
import RelationshipsConfirmation from "./RelationshipsConfirmation";

export default function FamilyRelationshipPickerWithUserProfile() {
  const history = useHistory();
  const location = useLocation();

  let match = useRouteMatch("*/user/:userID");
  const { userID: profileUserID } = match.params;

  const [
    {
      familyData: {
        relationshipUserID,
        relationshipConflicts,
        conflictsRelationshipUser,
        pendingRelationshipID,
        saveIndirectRelationships,
      },
    },
    dispatch,
  ] = useContext(UserProfileState);

  const removeAddFamilyMemberFromUrl = () => {
    let path = location.pathname;
    history.push(path.replace("/addFamilyMember", ""));
  };

  const closeOverlay = () => {
    dispatch({ type: "Hide Relationship Picker" });
    removeAddFamilyMemberFromUrl();
  };

  const handleUpdatedRelationships = (
    relationships,
    suggestedHouseholdMembers
  ) => {
    const dispatchData = { type: "Set Family Data", relationships };
    if (suggestedHouseholdMembers)
      dispatchData.suggestedFamilyMembers = suggestedHouseholdMembers;
    dispatch(dispatchData);
  };

  return (
    <FamilyRelationshipPicker
      profileUserID={profileUserID}
      relationshipUserID={relationshipUserID}
      relationshipConflicts={relationshipConflicts}
      conflictsRelationshipUser={conflictsRelationshipUser}
      pendingRelationshipID={pendingRelationshipID}
      saveIndirectRelationships={saveIndirectRelationships}
      closeOverlay={closeOverlay}
      handleUpdatedRelationships={handleUpdatedRelationships}
    />
  );
}

export function FamilyRelationshipPicker({
  profileUserID,
  relationshipUserID = null,
  relationshipConflicts = [],
  conflictsRelationshipUser = null,
  pendingRelationshipID = null,
  saveIndirectRelationships = true,
  closeOverlay = () => {},
  handleUpdatedRelationships = () => {},
  onFinish = () => {},
}) {
  const [loading, setLoading] = useState(false);
  const [shouldShow, setShouldShow] = useState(false);
  const [shouldSave, setShouldSave] = useState(false);
  const [saveDisabled, setSaveDisabled] = useState(false);

  const [everyoneGroupID, setEveryoneGroupID] = useState(0);
  const [profileUser, setProfileUser] = useState(null);
  const [relationshipUser, setRelationshipUser] = useState(
    conflictsRelationshipUser
  );
  const [relationshipTypeID, setRelationshipTypeID] = useState(null);
  const [availableRelationships, setAvailableRelationships] = useState({});

  const [paymentMethodsUnshared, setPaymentMethodsUnshared] = useState(false);

  const [householdRelationships, setHouseholdRelationships] = useState([]);

  const [conflicts, setConflicts] = useState(relationshipConflicts);
  const [forceSaveData, setForceSaveData] = useState({});

  const [createdHouseholdID, setCreatedHouseholdID] = useState(null);
  const [newlyCreatedRelationships, setNewlyCreatedRelationships] = useState(
    []
  );

  const [alertMsg, setAlertMsg] = useState("");
  const [showAlert, setShowAlert] = useState(false);
  const saveButtonRef = useRef();

  const [errorMsg, setErrorMsg] = useState("");

  //determine step
  let step;
  if (!relationshipUserID && !relationshipUser) step = "add_user";
  else if (householdRelationships.length > 1) step = "confirmation";
  else if (conflicts.length > 0) step = "conflicts";
  else if (paymentMethodsUnshared) step = "unsharedPaymentsWarning";
  else step = "relationship";

  useEffect(() => {
    if (!profileUserID) return;

    if (step === "add_user") {
      const getProfileUserInfo = API.getUserInfo(profileUserID);

      getProfileUserInfo.post().then(({ profileUser, everyoneGroupID }) => {
        setProfileUser(profileUser);
        setEveryoneGroupID(everyoneGroupID);
        if (step === "add_user") setShouldShow(true);
      });

      return () => getProfileUserInfo.cancel();
    } else if (step === "relationship" && relationshipUserID) {
      const getDataCall = API.getAddFamilyMemberData({
        profileUserID,
        relationshipUserID,
      });

      getDataCall
        .post()
        .then(
          ({
            relationships,
            profileUser,
            relationshipUser,
            relationshipTypeID,
            householdGroupingEnabled,
            envelopeNumberConflict,
            paymentMethodsUnshared,
          }) => {
            if (envelopeNumberConflict) {
              setShouldShow(false);
              alert(
                "It looks like both these people have different envelope numbers.  You'll need to delete the old envelope number from one of their profiles before adding them into the same household."
              );
            } else {
              relationships.suggested.push({
                ID: 0,
              });
              setAvailableRelationships(relationships);
              setProfileUser(profileUser);
              setRelationshipUser(relationshipUser);
              setRelationshipTypeID(relationshipTypeID);
              setShouldShow(true);
            }
          }
        );

      return () => getDataCall.cancel();
    }
  }, [profileUserID, relationshipUserID, step]);

  const handleRelationshipResponse = (
    {
      relationships,
      householdRelationships,
      conflicts,
      userCantAddHoh,
      sendingConfirmation,
      suggestedHouseholdMembers,
      currentUserIsAdmin,
      relationshipsToBeCreated,
      paymentMethodsUnshared: paymentsUnshared,
    },
    saveData
  ) => {
    setNewlyCreatedRelationships(relationshipsToBeCreated);
    setLoading(false);
    setShouldSave(false);
    setSaveDisabled(false);

    if (userCantAddHoh || sendingConfirmation) {
      const msg = userCantAddHoh
        ? "We're not able to add this person because they are the head of another household.  " +
          (currentUserIsAdmin
            ? "You will need to remove them from their other household before adding them to this one."
            : "Contact an admin to have them removed from the household.")
        : `We have sent an email to this user to confirm this relationship.`;
      alert(msg);
      handleAlertButtonClick();
      onFinish &&
        onFinish(
          relationshipUser,
          createdHouseholdID,
          newlyCreatedRelationships
        );
    } else if (conflicts?.length > 0) {
      setConflicts(conflicts);
      setForceSaveData(saveData);
    } else {
      setForceSaveData({});

      handleUpdatedRelationships(relationships, suggestedHouseholdMembers);

      if (householdRelationships?.length > 1) {
        setHouseholdRelationships(householdRelationships);
      } else if (!paymentsUnshared) {
        closeOverlay();
        setConflicts([]);
        onFinish &&
          onFinish(
            relationshipUser,
            createdHouseholdID,
            newlyCreatedRelationships
          );
      }
      setPaymentMethodsUnshared(paymentsUnshared);
    }
  };

  const handleAlertButtonClick = () => {
    setShowAlert(false);
    setConflicts([]);
    closeOverlay();
  };

  const closeAlertPopover = () => {
    setShowAlert(false);
    setAlertMsg(null);
  };

  const confirmPendingRelationship = () => {
    API.confirmPendingRelationship({
      relationshipID: pendingRelationshipID,
      confirmedByAdmin: true,
    })
      .post()
      .then((resp) => {
        handleRelationshipResponse(resp);
      });
  };

  const isPending =
    relationshipConflicts.length > 0 && conflictsRelationshipUser;

  if (!shouldShow && !isPending) return null;

  const savingProps = {
    loading,
    setLoading,
    setSaveDisabled,
    shouldSave,
    setShouldSave,
  };

  const handleErrorPopoverClose = () => {
    setErrorMsg("");
    setLoading(false);
    setSaveDisabled(false);
    setShouldSave(false);
  };

  const handleClose = () => {
    if (step === "confirmation" || step === "relationship") {
      onFinish(relationshipUser, createdHouseholdID, newlyCreatedRelationships);
    }
    closeOverlay();
  };

  const handleFinish = () => {
    if (step === "confirmation") {
      onFinish(relationshipUser, createdHouseholdID, newlyCreatedRelationships);
    }
    closeOverlay();
  };

  const handleSave = () => {
    setShouldSave(true);
  };

  const handleSaveClick = () => {
    if (step === "confirmation" || step === "unsharedPaymentsWarning")
      handleFinish();
    else if (isPending) confirmPendingRelationship();
    else if (!saveDisabled && !loading) handleSave();
  };

  return (
    <FnrOverlay componentName="Family" autoShow>
      <>
        <FnrOverlayContents>
          <SharedResizingContainer>
            {step === "add_user" && (
              <SharedResizingChild key={1}>
                <AddFamilyMemberByName
                  profileUserID={profileUserID}
                  lastName={profileUser.lname}
                  setRelationshipUser={setRelationshipUser}
                  setAvailableRelationships={setAvailableRelationships}
                  groupID={everyoneGroupID}
                  setShouldShow={setShouldShow}
                  closeOverlay={closeOverlay}
                  setAlertMsg={setAlertMsg}
                  setShowAlert={setShowAlert}
                  setCreatedHouseholdID={setCreatedHouseholdID}
                  {...savingProps}
                />
              </SharedResizingChild>
            )}
            {step === "relationship" && (
              <FamilyRelationshipPickerSection
                key={2}
                profileUser={profileUser}
                relationshipUser={relationshipUser}
                availableRelationships={availableRelationships}
                handleRelationshipResponse={handleRelationshipResponse}
                initialRelationshipTypeID={relationshipTypeID}
                fromConfirmationStep={step === "confirmation"}
                saveIndirectRelationships={saveIndirectRelationships}
                {...savingProps}
                setErrorMsg={setErrorMsg}
              />
            )}
            {step === "conflicts" && (
              <ConflictsConfirmation
                conflicts={conflicts}
                userFname={relationshipUser.fname}
                gender={relationshipUser.gender}
                saveData={forceSaveData}
                handleRelationshipResponse={handleRelationshipResponse}
                {...savingProps}
              />
            )}
            {step === "confirmation" && (
              <RelationshipsConfirmation
                householdRelationships={householdRelationships}
                relationshipUser={relationshipUser}
                paymentMethodsUnshared={paymentMethodsUnshared}
              />
            )}
            {step === "unsharedPaymentsWarning" && <UnsharedMessageContent />}
          </SharedResizingContainer>
          <FnrOverlayButtonRow>
            <FnrButton onClick={handleClose} disabled={loading}>
              {step === "confirmation" ? "Close" : "Cancel"}
            </FnrButton>

            <span ref={saveButtonRef}>
              <FnrButton
                color="blue"
                disabled={saveDisabled || loading}
                loading={loading}
                onClick={handleSaveClick}
              >
                {step === "user_info"
                  ? "Next"
                  : step === "conflicts"
                  ? "Add Anyway"
                  : step === "confirmation"
                  ? "Looks Good!"
                  : step === "unsharedPaymentsWarning"
                  ? "Got it"
                  : "Save"}
              </FnrButton>
              <ErrorPopover
                handleClose={handleErrorPopoverClose}
                message={errorMsg}
              />
            </span>

            <FnrConfirmationPopover
              show={showAlert}
              ref={saveButtonRef}
              onClose={closeAlertPopover}
              message={alertMsg}
              button1={
                <FnrButton text="OK" onClick={closeAlertPopover} color="blue" />
              }
              style={{ maxWidth: 200, padding: 10 }}
            />
          </FnrOverlayButtonRow>
        </FnrOverlayContents>
      </>
    </FnrOverlay>
  );
}
